import React from 'react';
import Button from '../../../../components/Atoms/Button';
import { useI18n } from '../../../../i18n';

const MIN_CLASSES = 2;
const MAX_CLASSES = 12;

/**
 * Turn a selection of extracted frames into something labelable.
 *
 * The same three outcomes the assistant offers for an uploaded zip —
 * classification, detection, or just the raw images — except the frames are
 * already in MinIO, so the AI runs server-side and nothing is re-uploaded.
 *
 * The destination half can CREATE its project and model, so a dataset built
 * from a video does not need one to exist first. When an existing model is
 * chosen the version dropdown defaults to its most recent, because a build
 * ADDS to what is already there.
 */
const TASKS = [
  { value: 'classification', labelKey: 'videoToImages.build.taskClassification', hintKey: 'videoToImages.build.taskClassificationHint', icon: 'fa-th-large' },
  { value: 'bounding_box', labelKey: 'videoToImages.build.taskDetection', hintKey: 'videoToImages.build.taskDetectionHint', icon: 'fa-vector-square' },
  { value: 'unlabeled', labelKey: 'videoToImages.build.taskUnlabeled', hintKey: 'videoToImages.build.taskUnlabeledHint', icon: 'fa-inbox' },
];

const BuildDatasetModal = ({
  isOpen,
  versionTarget,
  versionUnlabeled,
  datasetName,
  selectedCount,
  projects,
  transferModels,
  modelVersions,
  transferProjectId,
  transferModelId,
  transferVersionId,
  creatingProject,
  newProjectName,
  creatingModel,
  newModelName,
  newModelType,
  buildTask,
  buildClassMode,
  buildClassCount,
  buildClassNames,
  buildObjects,
  buildConfidence,
  isStartingBuild,
  handlers,
}) => {
  const { t } = useI18n();
  if (!isOpen) return null;

  /*
   * Two modes in one dialog.
   *
   * `versionTarget` means the images are ALREADY in a model version — a video
   * extraction that went straight into a model — so there is no destination to
   * choose and nothing is copied; the unlabeled rows are filled in. The normal
   * mode copies a dataset selection into a destination the user picks.
   */
  const inPlace = Boolean(versionTarget);
  const counting = inPlace && versionUnlabeled === null;
  const nothingToDo = inPlace && versionUnlabeled === 0;

  // "Just add them" is meaningless in place: the images are already there.
  const tasks = inPlace ? TASKS.filter((task) => task.value !== 'unlabeled') : TASKS;
  const activeTask = tasks.find((task) => task.value === buildTask) || tasks[0];
  const hasProject = creatingProject ? Boolean((newProjectName || '').trim()) : Boolean(transferProjectId);
  const hasModel = creatingModel ? Boolean((newModelName || '').trim()) : Boolean(transferModelId);
  const hasObjects = buildTask !== 'bounding_box' || Boolean((buildObjects || '').trim());
  const canStart = inPlace
    ? Boolean(hasObjects && versionUnlabeled > 0 && !isStartingBuild)
    : Boolean(hasProject && hasModel && hasObjects && selectedCount && !isStartingBuild);

  // A new model is created with the task's own type, so it cannot mismatch.
  // An existing one can, and the server refuses it — so say so here first.
  const chosenModel = transferModels.find((model) => model.model_id === transferModelId);
  const mismatch = Boolean(
    chosenModel && buildTask !== 'unlabeled' && chosenModel.model_type !== buildTask,
  );

  return (
    <div className="modal fade show v2i-modal" tabIndex="-1" role="dialog" onClick={handlers.handleCloseBuild}>
      <div className="modal-dialog modal-lg" role="document" onClick={(event) => event.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title h5 mb-0">
              {inPlace ? t('videoToImages.build.versionHeading') : t('videoToImages.build.heading')}
            </h5>
          </div>

          <div className="modal-body">
            <p className="v2i-hint">
              {inPlace
                ? (counting
                  ? t('common.loading')
                  : t('videoToImages.build.versionIntro', {
                    count: versionUnlabeled,
                    model: versionTarget.modelName,
                    version: versionTarget.version,
                  }))
                : t('videoToImages.build.intro', { count: selectedCount, dataset: datasetName })}
            </p>

            {nothingToDo && (
              <div className="v2i-alert v2i-alert--info">
                <i className="fa fa-info-circle mr-1" aria-hidden="true"></i>
                {t('videoToImages.build.versionNothing')}
              </div>
            )}

            {/* ---- 1. what to make ---- */}
            <h6 className="v2i-section-title">{t('videoToImages.build.taskHeading')}</h6>
            <div className="v2i-task-grid" role="radiogroup" aria-label={t('videoToImages.build.taskHeading')}>
              {tasks.map((task) => (
                <button
                  key={task.value}
                  type="button"
                  role="radio"
                  aria-checked={buildTask === task.value}
                  className={`v2i-task-card ${buildTask === task.value ? 'v2i-task-card--active' : ''}`}
                  disabled={isStartingBuild}
                  onClick={() => handlers.setBuildTask(task.value)}
                >
                  <i className={`fa ${task.icon} v2i-task-card__icon`} aria-hidden="true"></i>
                  <span className="v2i-task-card__label">{t(task.labelKey)}</span>
                </button>
              ))}
            </div>
            <p className="v2i-hint">{t(activeTask.hintKey)}</p>

            {/* ---- classification options ---- */}
            {buildTask === 'classification' && (
              <div className="v2i-field-group">
                <span className="v2i-label">{t('videoToImages.build.classesHeading')}</span>
                <div className="v2i-segmented" role="radiogroup"
                  aria-label={t('videoToImages.build.classesHeading')}>
                  {[
                    { value: 'auto', labelKey: 'videoToImages.build.classesAuto' },
                    { value: 'fixed', labelKey: 'videoToImages.build.classesFixed' },
                  ].map((mode) => (
                    <button
                      key={mode.value}
                      type="button"
                      role="radio"
                      aria-checked={buildClassMode === mode.value}
                      className={`v2i-segmented__item ${buildClassMode === mode.value ? 'v2i-segmented__item--active' : ''}`}
                      disabled={isStartingBuild}
                      onClick={() => handlers.setBuildClassMode(mode.value)}
                    >
                      {t(mode.labelKey)}
                    </button>
                  ))}
                </div>

                {buildClassMode === 'fixed' && (
                  <div className="mt-2">
                    <label className="v2i-label" htmlFor="v2i-build-count">
                      {t('videoToImages.build.classCount')}
                    </label>
                    <input
                      id="v2i-build-count"
                      type="number"
                      className="form-control"
                      min={MIN_CLASSES}
                      max={MAX_CLASSES}
                      step={1}
                      value={buildClassCount}
                      disabled={isStartingBuild}
                      onChange={(event) => handlers.setBuildClassCount(
                        event.target.value === '' ? '' : Number(event.target.value),
                      )}
                    />
                  </div>
                )}

                <div className="mt-2">
                  <label className="v2i-label" htmlFor="v2i-build-names">
                    {t('videoToImages.build.classNames')}
                  </label>
                  <input
                    id="v2i-build-names"
                    type="text"
                    className="form-control"
                    placeholder={t('videoToImages.build.classNamesPlaceholder')}
                    value={buildClassNames}
                    disabled={isStartingBuild}
                    onChange={(event) => handlers.setBuildClassNames(event.target.value)}
                  />
                  <p className="v2i-hint mb-0">{t('videoToImages.build.classNamesHint')}</p>
                </div>
              </div>
            )}

            {/* ---- detection options ---- */}
            {buildTask === 'bounding_box' && (
              <div className="v2i-field-group">
                <label className="v2i-label" htmlFor="v2i-build-objects">
                  {t('videoToImages.build.objectsHeading')}
                </label>
                <input
                  id="v2i-build-objects"
                  type="text"
                  className="form-control"
                  placeholder={t('videoToImages.build.objectsPlaceholder')}
                  value={buildObjects}
                  disabled={isStartingBuild}
                  onChange={(event) => handlers.setBuildObjects(event.target.value)}
                />
                <p className="v2i-hint mb-0">{t('videoToImages.build.objectsHint')}</p>

                <label className="v2i-label mt-2" htmlFor="v2i-build-conf">
                  {t('videoToImages.build.confidence')} — <strong>{buildConfidence}</strong>
                </label>
                <input
                  id="v2i-build-conf"
                  type="range"
                  className="v2i-range"
                  min={0.05}
                  max={0.9}
                  step={0.05}
                  value={buildConfidence}
                  disabled={isStartingBuild}
                  onChange={(event) => handlers.setBuildConfidence(Number(event.target.value))}
                />
                <p className="v2i-hint mb-0">{t('videoToImages.build.confidenceHint')}</p>
              </div>
            )}

            {/* ---- 2. destination ---- */}
            <h6 className="v2i-section-title">
              {inPlace
                ? t('videoToImages.build.versionTarget')
                : t('videoToImages.build.destinationHeading')}
            </h6>

            {inPlace ? (
              <>
                <p className="v2i-build-card__result mb-1">
                  <i className="fa fa-cube mr-1" aria-hidden="true"></i>
                  {versionTarget.modelName}
                  {' · '}
                  {t('videoToImages.jobs.version', { version: versionTarget.version })}
                </p>
                <div className="v2i-alert v2i-alert--info mb-0">
                  <i className="fa fa-info-circle mr-1" aria-hidden="true"></i>
                  {t('videoToImages.build.versionNote')}
                </div>
              </>
            ) : (
              <>

            <div className="mb-2">
              <label className="v2i-label" htmlFor="v2i-build-project">
                {t('videoToImages.transfer.project')}
              </label>
              {creatingProject ? (
                <div className="v2i-inline-create">
                  <input
                    id="v2i-build-new-project"
                    type="text"
                    className="form-control"
                    autoFocus
                    maxLength={60}
                    placeholder={t('videoToImages.target.newProjectPlaceholder')}
                    value={newProjectName}
                    disabled={isStartingBuild}
                    onChange={(event) => handlers.setNewProjectName(event.target.value)}
                  />
                  <Button variant="ghost" size="sm" disabled={isStartingBuild}
                    onClick={() => handlers.setCreatingProject(false)}>
                    {t('videoToImages.target.cancelCreate')}
                  </Button>
                </div>
              ) : (
                <div className="v2i-inline-create">
                  <select
                    id="v2i-build-project"
                    className="form-control"
                    value={transferProjectId}
                    disabled={isStartingBuild}
                    onChange={(event) => handlers.handleTransferProjectChange(event.target.value)}
                  >
                    <option value="">{t('videoToImages.transfer.selectProject')}</option>
                    {projects.map((project) => (
                      <option key={project._id} value={project._id}>{project.name}</option>
                    ))}
                  </select>
                  <Button variant="outline-secondary" size="sm" disabled={isStartingBuild}
                    onClick={() => handlers.setCreatingProject(true)}>
                    {t('videoToImages.target.newProject')}
                  </Button>
                </div>
              )}
            </div>

            <div className="mb-2">
              <label className="v2i-label" htmlFor="v2i-build-model">
                {t('videoToImages.transfer.model')}
              </label>
              {creatingModel ? (
                <>
                  <div className="v2i-inline-create">
                    <input
                      id="v2i-build-new-model"
                      type="text"
                      className="form-control"
                      autoFocus
                      maxLength={60}
                      placeholder={t('videoToImages.target.newModelPlaceholder')}
                      value={newModelName}
                      disabled={isStartingBuild}
                      onChange={(event) => handlers.setNewModelName(event.target.value)}
                    />
                    <Button variant="ghost" size="sm" disabled={isStartingBuild}
                      onClick={() => handlers.setCreatingModel(false)}>
                      {t('videoToImages.target.cancelCreate')}
                    </Button>
                  </div>
                  {/* Only asked when nothing else decides it: an AI task names
                      the model's own kind. */}
                  {buildTask === 'unlabeled' && (
                    <div className="v2i-segmented mt-2" role="radiogroup"
                      aria-label={t('videoToImages.target.modelKind')}>
                      {[
                        { value: 'classification', labelKey: 'videoToImages.target.kindClassification' },
                        { value: 'bounding_box', labelKey: 'videoToImages.target.kindDetection' },
                      ].map((kind) => (
                        <button
                          key={kind.value}
                          type="button"
                          role="radio"
                          aria-checked={newModelType === kind.value}
                          className={`v2i-segmented__item ${newModelType === kind.value ? 'v2i-segmented__item--active' : ''}`}
                          disabled={isStartingBuild}
                          onClick={() => handlers.setNewModelType(kind.value)}
                        >
                          {t(kind.labelKey)}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="v2i-inline-create">
                  <select
                    id="v2i-build-model"
                    className="form-control"
                    value={transferModelId}
                    disabled={isStartingBuild || !transferProjectId || !transferModels.length}
                    onChange={(event) => handlers.handleTransferModelChange(event.target.value)}
                  >
                    <option value="">
                      {transferProjectId && !transferModels.length
                        ? t('videoToImages.transfer.noModels')
                        : t('videoToImages.transfer.selectModel')}
                    </option>
                    {transferModels.map((model) => (
                      <option key={model.model_id} value={model.model_id}>
                        {model.model_name} ({model.model_type})
                      </option>
                    ))}
                  </select>
                  <Button variant="outline-secondary" size="sm"
                    disabled={isStartingBuild || !transferProjectId}
                    onClick={() => handlers.setCreatingModel(true)}>
                    {t('videoToImages.target.newModel')}
                  </Button>
                </div>
              )}
            </div>

            {mismatch && (
              <div className="v2i-alert v2i-alert--warning">
                <i className="fa fa-exclamation-triangle mr-1" aria-hidden="true"></i>
                {t('videoToImages.build.taskHeading')} — {chosenModel.model_name} ({chosenModel.model_type})
              </div>
            )}

            {!creatingModel && transferModelId && (
              <div className="mb-2">
                <label className="v2i-label" htmlFor="v2i-build-version">
                  {t('videoToImages.transfer.version')}
                </label>
                <select
                  id="v2i-build-version"
                  className="form-control"
                  value={transferVersionId}
                  disabled={isStartingBuild}
                  onChange={(event) => handlers.setTransferVersionId(event.target.value)}
                >
                  {modelVersions.map((version) => (
                    <option key={version._id} value={version._id}>
                      {t('videoToImages.transfer.versionLatest', {
                        version: version.version, count: version.imageCount,
                      })}
                    </option>
                  ))}
                  <option value="new">{t('videoToImages.transfer.versionNew')}</option>
                </select>
              </div>
            )}

            <div className="v2i-alert v2i-alert--info mb-0">
              <i className="fa fa-info-circle mr-1" aria-hidden="true"></i>
              {t('videoToImages.build.appendNote')}
            </div>
              </>
            )}
          </div>

          <div className="modal-footer">
            <Button variant="secondary" size="sm" disabled={isStartingBuild}
              onClick={handlers.handleCloseBuild}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" size="sm" disabled={!canStart || mismatch}
              onClick={handlers.handleStartBuild}>
              {isStartingBuild
                ? <><i className="fa fa-spinner fa-spin mr-1" aria-hidden="true"></i>{t('videoToImages.build.starting')}</>
                : t('videoToImages.build.start')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuildDatasetModal;
